import * as React from "react";

// useRealtime — the console's consumer of the realtime bus.
//
// modwatch and measured emit health transitions, unit stopped/started/crash-loop/OOM and
// threshold breaches, notify pushes the loud ones to devices — and NOTHING in the console was
// listening, so an operator with the panel open saw nothing until they reloaded a page. The
// notification centre was assembled by POLLING /ozpanel/modules and /backups/status, which
// reports a state but never an EVENT: a service that crashed and recovered between two polls
// left no trace at all.
//
// EventSource, not fetch: it reconnects on its own, and the endpoint is same-origin so the
// session cookie rides along (EventSource cannot set headers, which is exactly why
// /realtime/subscribe is cookie-authenticated).
//
// In the KIT because all three consoles need it and only the admin one had it. The customer
// consoles polled — a bell that refreshes every thirty seconds tells someone about a full mailbox
// half a minute after it happened, and tells them nothing at all if they are reading a page in
// between. Same endpoint, same scope: the bus filters by the subscriber's own principal, so a
// tenant's stream carries a tenant's events without the consoles agreeing on anything.

export interface BusEvent {
  seq: number;
  module: string;
  kind: string;
  priority: "info" | "warn" | "error" | "crit";
  category: string;
  summary: string;
  detail?: string;
  consequence?: string;
  /** One line of what to look at or do (realtime.Event.Next). */
  next?: string;
  scope?: { node?: string };
  at: string;
  payload?: Record<string, unknown>;
}

const MAX = 200; // bounded: this is a live tail, not a store — history is S0.2's job

export function useRealtime(basePath = "/realtime/subscribe") {
  const [events, setEvents] = React.useState<BusEvent[]>([]);
  const [connected, setConnected] = React.useState(false);

  React.useEffect(() => {
    let es: EventSource | null = null;
    let stopped = false;
    let retry: ReturnType<typeof setTimeout> | undefined;

    const open = () => {
      if (stopped) return;
      es = new EventSource(basePath, { withCredentials: true });
      es.onopen = () => setConnected(true);
      es.onmessage = (m) => {
        try {
          const ev = JSON.parse(m.data) as BusEvent;
          if (!ev || typeof ev.seq !== "number") return;
          setEvents((prev) => {
            // The bus replays a backlog on connect, so the same seq can arrive twice across a
            // reconnect. Dedupe on it rather than showing an event twice.
            if (prev.some((p) => p.seq === ev.seq)) return prev;
            return [ev, ...prev].slice(0, MAX);
          });
        } catch {
          /* a frame we cannot parse is not worth tearing the stream down for */
        }
      };
      es.onerror = () => {
        setConnected(false);
        es?.close();
        // EventSource retries on its own, but only while the connection was ever established;
        // an auth failure closes it for good, so reconnect explicitly.
        if (!stopped) retry = setTimeout(open, 5000);
      };
    };
    open();

    return () => {
      stopped = true;
      if (retry) clearTimeout(retry);
      es?.close();
    };
  }, [basePath]);

  return { events, connected };
}

/** The module an event is ABOUT. Platform-generated events (modwatch) are emitted under the
 *  reserved "ozpanel" identity and carry the watched module in the payload. */
export function eventModule(e: BusEvent): string {
  const m = e.payload?.module;
  return typeof m === "string" && m ? m : e.module;
}
